/**
 * Sampling an animation into a local pose (docs/SKEL-EDITOR.md §5.1 `animate.ts`, report 01 §6.2–6.3).
 *
 * rusty_skelform's `animate` mutates a persistent armature: every key whose segment covers the
 * frame writes its channel, the last write wins, and channels no passed animation keys reset to
 * `init_*`. This module is the stateless equivalent: start from the rest pose, then for every
 * (bone, element) channel take the LAST key (array order after a stable frame sort) whose frame
 * ≤ f — exactly the key rusty processes last — and interpolate towards the next key with a
 * strictly greater frame using that destination key's handles. A channel whose first key lies
 * after f keeps its rest value (our writers always emit a frame-0 key). It never trusts the stored
 * `next_kf`, so a half-edited draft still samples correctly.
 *
 * Differences from rusty, all deliberate (report 01 §8.3, §9.2): fractional frames; `Texture`
 * and `Zindex` step keys are applied (rusty's `"Tex"` match never fires); `IkMode` is applied;
 * no `smoothFrames` blending (use `blendPoses` for a real cross-fade). Loop semantics are the
 * editor's (D12): an animation's length is its last key's frame, period = length.
 */
import { clamp, interpolate, lerp, lerp2, shortestDelta } from './math'
import { constraintFromValue } from './normalize'
import type { AnimElement, IkConstraint, IkMode, SkfAnimation, SkfKeyframe, SkfRoot, Tint, V2 } from './types'

export interface PoseBone {
  pos: V2
  rot: number
  scale: V2
  hidden: boolean
}

export interface PoseVisual {
  tex: string
  zindex: number
  tint: Tint
}

export interface PoseIk {
  constraint: IkConstraint
  mode: IkMode
  mimic_target: boolean
}

/** A local (parent-relative) pose: index-aligned with `root.bones`, `root.visuals`, `root.inverse_kinematics`. */
export interface Pose {
  bones: PoseBone[]
  visuals: PoseVisual[]
  ik: PoseIk[]
}

export interface SampleOptions {
  /** Frames outside [0, length] wrap with period = length (editor loop, D12); else clamp. Default true. */
  loop?: boolean
  /**
   * `channelsOf(anim)`, precomputed by a caller whose animations never change
   * (the story player caches it per animation). Absent = built on every call,
   * which is right for the editor, where keys are edited in place.
   */
  channels?: ReadonlyMap<string, SkfKeyframe[]>
}

const WHITE: Tint = { r: 1, g: 1, b: 1, a: 1 }

/** The rest pose (`init_*` values, falling back to the current field for pre-0.7 data). */
export function restPose(root: SkfRoot): Pose {
  return {
    bones: root.bones.map((b) => {
      const pos = b.init_pos ?? b.pos
      const scale = b.init_scale ?? b.scale
      return {
        pos: { x: pos.x, y: pos.y },
        rot: b.init_rot ?? b.rot,
        scale: { x: scale.x, y: scale.y },
        hidden: (b.init_hidden ?? b.hidden) === true,
      }
    }),
    visuals: root.visuals.map((v) => {
      const t = v.init_tint ?? v.tint ?? WHITE
      return { tex: v.init_tex ?? v.tex, zindex: v.init_zindex ?? v.zindex, tint: { r: t.r, g: t.g, b: t.b, a: t.a } }
    }),
    ik: root.inverse_kinematics.map((ik) => ({
      constraint: ik.init_constraint ?? ik.constraint ?? 'None',
      mode: ik.init_mode ?? ik.mode ?? 'FABRIK',
      mimic_target: (ik.init_mimic_target ?? ik.mimic_target) === true,
    })),
  }
}

/** Deep copy of a pose (poses are plain data). */
export function clonePose(p: Pose): Pose {
  return {
    bones: p.bones.map((b) => ({ pos: { ...b.pos }, rot: b.rot, scale: { ...b.scale }, hidden: b.hidden })),
    visuals: p.visuals.map((v) => ({ tex: v.tex, zindex: v.zindex, tint: { ...v.tint } })),
    ik: p.ik.map((k) => ({ ...k })),
  }
}

/** Last keyed frame (the max frame; 0 for an empty animation). */
export function animLength(anim: SkfAnimation | null | undefined): number {
  if (!anim) return 0
  let m = 0
  for (const kf of anim.keyframes) if (kf.frame > m) m = kf.frame
  return m
}

/** Wrap or clamp a (fractional) frame the way `samplePose` does. */
export function wrapFrame(anim: SkfAnimation | null | undefined, frame: number, loop: boolean): number {
  const length = animLength(anim)
  if (!Number.isFinite(frame)) return 0
  if (loop) return length > 0 ? ((frame % length) + length) % length : 0
  return clamp(frame, 0, Math.max(length, 0))
}

/** Seconds of playback → fractional frame (no rounding; period = length when looping). */
export function frameAtTime(anim: SkfAnimation | null | undefined, seconds: number, loop: boolean): number {
  if (!anim) return 0
  return wrapFrame(anim, seconds * (anim.fps > 0 ? anim.fps : 60), loop)
}

// ------------------------------------------------------------------------------------ sampling

/** The segment covering `frame` on one channel: start key (last with frame ≤ f) and destination. */
interface Segment {
  from: SkfKeyframe
  to: SkfKeyframe | null
}

/** Group the keys of an animation per `bone|element` channel, each list in stable frame order. */
export function channelsOf(anim: SkfAnimation): Map<string, SkfKeyframe[]> {
  const out = new Map<string, SkfKeyframe[]>()
  const indexed = anim.keyframes.map((kf, i) => ({ kf, i }))
  indexed.sort((a, b) => a.kf.frame - b.kf.frame || a.i - b.i)
  for (const { kf } of indexed) {
    const key = `${kf.bone_id}|${kf.element}`
    const list = out.get(key)
    if (list) list.push(kf)
    else out.set(key, [kf])
  }
  return out
}

function segmentAt(keys: SkfKeyframe[], frame: number): Segment | null {
  let i = -1
  for (let k = 0; k < keys.length; k++) {
    if (keys[k]!.frame <= frame) i = k
    else break
  }
  if (i < 0) return null
  const from = keys[i]!
  let to: SkfKeyframe | null = null
  for (let k = i + 1; k < keys.length; k++) {
    if (keys[k]!.frame > from.frame) {
      to = keys[k]!
      break
    }
  }
  return { from, to }
}

function segmentValue(seg: Segment, frame: number): number {
  if (!seg.to) return seg.from.value
  return interpolate(frame - seg.from.frame, seg.to.frame - seg.from.frame, seg.from.value, seg.to.value, seg.to.start_handle, seg.to.end_handle)
}

const IK_MODES: readonly IkMode[] = ['FABRIK', 'Arc']

function applyKey(root: SkfRoot, pose: Pose, element: AnimElement | string, seg: Segment, frame: number): void {
  const kf = seg.from
  const bone = pose.bones[kf.bone_id]
  const def = root.bones[kf.bone_id]
  if (!bone || !def) return
  switch (element) {
    case 'PositionX':
      bone.pos.x = segmentValue(seg, frame)
      return
    case 'PositionY':
      bone.pos.y = segmentValue(seg, frame)
      return
    case 'Rotation':
      bone.rot = segmentValue(seg, frame)
      return
    case 'ScaleX':
      bone.scale.x = segmentValue(seg, frame)
      return
    case 'ScaleY':
      bone.scale.y = segmentValue(seg, frame)
      return
    case 'Hidden':
      bone.hidden = kf.value === 1
      return
  }
  const vis = def.visuals_id >= 0 ? pose.visuals[def.visuals_id] : undefined
  if (vis) {
    switch (element) {
      case 'TintR':
        vis.tint.r = segmentValue(seg, frame)
        return
      case 'TintG':
        vis.tint.g = segmentValue(seg, frame)
        return
      case 'TintB':
        vis.tint.b = segmentValue(seg, frame)
        return
      case 'TintA':
        vis.tint.a = segmentValue(seg, frame)
        return
      case 'Texture':
        vis.tex = kf.value_str ?? ''
        return
      case 'Zindex':
        vis.zindex = Math.round(kf.value)
        return
    }
  }
  const ik = def.ik_family_id >= 0 ? pose.ik[def.ik_family_id] : undefined
  if (ik) {
    switch (element) {
      case 'IkConstraint':
        ik.constraint = (kf.value_str as IkConstraint | undefined) || constraintFromValue(kf.value)
        return
      case 'IkMode':
        if (IK_MODES.includes(kf.value_str as IkMode)) ik.mode = kf.value_str as IkMode
        return
      case 'MimicTarget':
        ik.mimic_target = kf.value === 1
        return
    }
  }
}

/**
 * The local pose of `root` at `frame` of animation `animIndex` (null/−1/out of range = rest).
 * Frames inside [0, length] are sampled as given, so the playhead parked ON the last key shows
 * that key even with `loop` on (scrubbing a one-shot such as `sit` to its end, or editing the last
 * key of a loop); with `loop` (default true) frames outside that range wrap with period = length
 * (D12), without it they clamp (past the end holds the last keyed values). Playback time goes
 * through `frameAtTime`, which already lands in [0, length).
 */
export function samplePose(root: SkfRoot, animIndex: number | null, frame: number, opts: SampleOptions = {}): Pose {
  const pose = restPose(root)
  if (animIndex === null || animIndex < 0) return pose
  const anim = root.animations?.[animIndex]
  if (!anim || anim.keyframes.length === 0) return pose
  const inRange = Number.isFinite(frame) && frame >= 0 && frame <= animLength(anim)
  const f = inRange ? frame : wrapFrame(anim, frame, opts.loop ?? true)
  for (const [key, keys] of opts.channels ?? channelsOf(anim)) {
    const seg = segmentAt(keys, f)
    if (!seg) continue
    applyKey(root, pose, key.slice(key.indexOf('|') + 1), seg, f)
  }
  return pose
}

/**
 * Value of one channel at `frame` (no looping), or null when the channel has no key at or before
 * `frame`. Step elements return the held key's `value`.
 */
export function keyValueAt(anim: SkfAnimation, boneId: number, element: AnimElement, frame: number): number | null {
  const keys = anim.keyframes
    .map((kf, i) => ({ kf, i }))
    .filter((e) => e.kf.bone_id === boneId && e.kf.element === element)
    .sort((a, b) => a.kf.frame - b.kf.frame || a.i - b.i)
    .map((e) => e.kf)
  const seg = segmentAt(keys, frame)
  if (!seg) return null
  if (element === 'Hidden' || element === 'Texture' || element === 'Zindex' || element === 'IkConstraint' || element === 'IkMode' || element === 'MimicTarget') {
    return seg.from.value
  }
  return segmentValue(seg, frame)
}

/**
 * Cross-fade two poses: w = 0 → a, w = 1 → b. Positions/scales/tints lerp, rotations take the
 * shortest arc, step fields (hidden, texture, z-index, IK settings) switch at w ≥ 0.5.
 */
export function blendPoses(a: Pose, b: Pose, w: number): Pose {
  const t = Number.isFinite(w) ? clamp(w, 0, 1) : 0
  const step = t >= 0.5
  return {
    bones: a.bones.map((ab, i) => {
      const bb = b.bones[i] ?? ab
      return {
        pos: lerp2(ab.pos, bb.pos, t),
        rot: ab.rot + shortestDelta(ab.rot, bb.rot) * t,
        scale: lerp2(ab.scale, bb.scale, t),
        hidden: step ? bb.hidden : ab.hidden,
      }
    }),
    visuals: a.visuals.map((av, i) => {
      const bv = b.visuals[i] ?? av
      return {
        tex: step ? bv.tex : av.tex,
        zindex: step ? bv.zindex : av.zindex,
        tint: {
          r: lerp(av.tint.r, bv.tint.r, t),
          g: lerp(av.tint.g, bv.tint.g, t),
          b: lerp(av.tint.b, bv.tint.b, t),
          a: lerp(av.tint.a, bv.tint.a, t),
        },
      }
    }),
    ik: a.ik.map((ak, i) => {
      const bk = b.ik[i] ?? ak
      return step ? { ...bk } : { ...ak }
    }),
  }
}
